import {api} from './base';
import {handleApiError} from './util';
import {
    CardInfoResponseData,
    ClientInfoResponseData,
    ContractInfoResponseData,
    CreateClientTaxInvoiceRequest,
    CreateWorkerRequest,
    DocumentInfoResponseData,
    DocumentTableResponseData,
    GetClientContractTableResponseData,
    GetClientPaidPaymentResponse,
    InfoForPayRequest,
    PaymentInfoResponseData,
    PaymentTableResponseData,
    PayRequest,
    ProjectDashInfoResponseData,
    ProjectInfoResponseData,
    ProjectNoteResponseData,
    ProjectRequest,
    ProjectRequestInfoResponseData,
    ProjectRequestTableResponseData,
    ProjectTableResponseData,
    RequestDashInfoResponseData,
    UpdateClientInfoRequest,
    UpdateProjectRequest
} from '../types/client';
import {ApiResponse, PageResponse} from "../types/response";
import {ProjectRequestSort, ProjectSort, ProjectStatus} from "../types/project";
import {DocumentSort, DocumentTarget} from "../types/document";
import {RequestStatus} from "../types/request";
import {ChatRequestFromFront, GetGraphResponseData, LicenseFileResponseData} from "../types/admin";
import {PaymentType} from "../types/contract";

export const createProjectRequestAPI = async (data: ProjectRequest, file: File | null) => { //프로젝트 추가 요청 생성 API 호출
    try {
        const formData = new FormData();
        formData.append('createProjectRequestRequest', new Blob([JSON.stringify(data)], {type: 'application/json'}));
        if (file) {
            formData.append('file', file);
        }
        const response = await api.post(`/request/project`, formData, {headers: {'Content-Type': 'multipart/form-data'},});
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const createWorkerRequestAPI = async (request: CreateWorkerRequest) => { //클라이언트용 작업자 추가 요청 생성 API 호출
    try {
        const response = await api.post(`/request/worker`, request);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const updateClientInfoAPI = async (request: UpdateClientInfoRequest, file: File | null) => { //클라이언트 정보 수정 API 호출
    try {
        const formData = new FormData();
        formData.append('updateClientInfoRequest', new Blob([JSON.stringify(request)], {type: 'application/json'}));
        if (file) {
            formData.append('license', file);
        }
        const response = await api.patch(`/client/info`, formData, {headers: {'Content-Type': 'multipart/form-data'},});
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const updateProjectRequestAPI = async (request: UpdateProjectRequest, file: File | null) => { //클라이언트 프로젝트 추가 요청 수정 API 호출
    try {
        const formData = new FormData();
        formData.append('updateProjectRequestRequest', new Blob([JSON.stringify(request)], {type: 'application/json'}));
        if (file) {
            formData.append('file', file);
        }
        const response = await api.patch(`/projectRequest/client`, formData, {headers: {'Content-Type': 'multipart/form-data'},});
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const deleteProjectRequestAPI = async (projectRequestId: number) => { //클라이언트 프로젝트 추가 요청 삭제 API 호출
    try {
        const response = await api.delete(`/projectRequest/client?requestId=${projectRequestId}`);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const requestAgainAPI = async (requestId: number) => { //클라이언트 프로젝트 추가 요청 재요청 API 호출
    try {
        const response = await api.patch(`/projectRequest/client/status?requestId=${requestId}`);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const documentTableAPI = async (params: {
    page: number,
    sort?: DocumentSort,
    target?: DocumentTarget,
    search?: string
}): Promise<PageResponse<DocumentTableResponseData[]>> => { //클라이언트 문서 테이블 조회 API 호출
    const searchParams = new URLSearchParams({
        page: params.page.toString(),
        sort: params.sort || ProjectSort.CREATE_ASC,
        target: params.target || DocumentTarget.ALL,
        search: params.search || ''
    })
    try {
        const response = await api.get<ApiResponse<PageResponse<DocumentTableResponseData[]>>>(`/document/client/table?${searchParams}`);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const projectRequestTableAPI = async (params: { //클라이언트 프로젝트 추가 요청 테이블 조회 API 호출
    page: number,
    sort?: ProjectRequestSort,
    status?: RequestStatus[],
    create?: string,
    update?: string,
    confirm?: string
})
    : Promise<PageResponse<ProjectRequestTableResponseData[]>> => {
    const searchParams = new URLSearchParams({
        page: params.page.toString(),
        sort: params.sort || ProjectSort.NAME_ASC,
        status: params.status?.join(',') || '예정,진행중,완료,추가견적',
        create: params.create || 'ALL',
        start: params.update || 'ALL',
        end: params.confirm || 'ALL'
    });
    try {
        const response = await api.get<ApiResponse<PageResponse<ProjectRequestTableResponseData[]>>>(`/projectRequest/client/table?${searchParams}`);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const clientInfoAPI = async (): Promise<ClientInfoResponseData> => { //클라이언트 정보 조회 API 호출
    try {
        const response = await api.get<ApiResponse<ClientInfoResponseData>>('/client/info');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const clientLicenseAPI = async (): Promise<LicenseFileResponseData> => { //클라이언트 사업자등록증 조회 API 호출
    try {
        const response = await api.get(`/client/info/license`, {
            responseType: 'blob'
        });
        return {data: response.data, extension: response.headers["file-extension"]};
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const projectRequestListAPI = async (): Promise<number[]> => { //클라이언트 프로젝트 추가 요청 식별자 목록 조회 API 호출
    try {
        const response = await api.get<ApiResponse<number[]>>('/projectRequest/client/project-request-id');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const projectTableAPI = async (params: { //클라이언트 프로젝트 테이블 조회 API 호출
    page: number, sort?: ProjectSort, status?: ProjectStatus[], create?: string, start?: string, end?: string
})
    : Promise<PageResponse<ProjectTableResponseData[]>> => {
    const searchParams = new URLSearchParams({
        page: params.page.toString(),
        sort: params.sort || ProjectSort.NAME_ASC,
        status: params.status?.join(',') || '예정,진행중,완료',
        create: params.create || 'ALL',
        start: params.start || 'ALL',
        end: params.end || 'ALL'
    });
    try {
        const response = await api.get<ApiResponse<PageResponse<ProjectTableResponseData[]>>>(`/project/client/table?${searchParams}`);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const projectListAPI = async (): Promise<number[]> => { //클라이언트 프로젝트 식별자 목록 조회 API 호출
    try {
        const response = await api.get<ApiResponse<number[]>>('/project/client/project-id');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const projectRequestInfoAPI = async (projectRequestId: number): Promise<ProjectRequestInfoResponseData> => { //클라이언트 프로젝트 추가 요청 단일 조회 API 호출
    try {
        const response = await api.get<ApiResponse<ProjectRequestInfoResponseData>>('/projectRequest/client?projectRequestId=' + (projectRequestId));
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const projectRequestSampleAPI = async (projectRequestId: number): Promise<Blob> => { //클라이언트 프로젝트 추가 요청 샘플 데이터 조회 API 호출
    try {
        const response = await api.get(`/projectRequest/client/sample?projectRequestId=${projectRequestId}`, {responseType: 'blob'});
        return response.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const projectInfoAPI = async (projectId: number): Promise<ProjectInfoResponseData> => { //클라이언트 프로젝트 단일 조회 API 호출
    try {
        const response = await api.get<ApiResponse<ProjectInfoResponseData>>('/project/client/info?projectId=' + (projectId));
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const projectSampleAPI = async (projectId: number): Promise<Blob> => { //클라이언트 프로젝트 샘플 데이터 조회 API 호출
    try {
        const response = await api.get(`/project/client/sample?projectId=${projectId}`, {responseType: 'blob'});
        return response.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const projectNoteAPI = async (projectId: number): Promise<ProjectNoteResponseData> => { //클라이언트 단일 프로젝트 특이사항 조회 API 호출
    try {
        const response = await api.get<ApiResponse<ProjectNoteResponseData>>('/project/client/note?projectId=' + (projectId));
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const isProjectNoteAvailableAPI = async (projectId: number): Promise<boolean> => { //클라이언트 단일 프로젝트 특이사항 조회 가능 여부 확인 API 호출
    try {
        const response = await api.get<ApiResponse<boolean>>('/project/client/available?projectId=' + (projectId));
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const projectNoteImageAPI = async (projectId: number): Promise<Blob> => { //클라이언트 단일 프로젝트 특이사항 샘플 이미지 조회 API 호출
    try {
        const response = await api.get(`/project/client/note-image?projectId=${projectId}`, {responseType: 'blob'});
        return response.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}


export const documentAPI = async (documentId: number): Promise<DocumentInfoResponseData> => { //클라이언트 문서 단일 조회 API 호출
    try {
        const response = await api.get<ApiResponse<DocumentInfoResponseData>>('/document/client?scopeId=' + (documentId));
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const getDocumentFileAPI = async (documentId: number): Promise<Blob> => { //클라이언트 문서 첨부파일 조회 API 호출
    try {
        const response = await api.get(`/document/client/file?scopeId=${documentId}`, {responseType: 'blob'});
        return response.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
};

export const projectDashInfoAPI = async (page: number, size: number): Promise<PageResponse<ProjectDashInfoResponseData[]>> => { //클라이언트 메인 대시보드 프로젝트 테이블 조회 API 호출
    try {
        const response = await api.get<ApiResponse<PageResponse<ProjectDashInfoResponseData[]>>>('/project/main/projects?page=' + (page - 1) + "&size=" + size);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const requestDashInfoAPI = async (page: number, size: number): Promise<PageResponse<RequestDashInfoResponseData[]>> => { //클라이언트 메인 대시보드 요청 현황 테이블 조회 API 호출
    try {
        const response = await api.get<ApiResponse<PageResponse<RequestDashInfoResponseData[]>>>('/request/client?page=' + (page - 1) + "&size=" + size);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const workerAmountAPI = async (): Promise<number> => { //클라이언트 작업자 수 조회 API 호출
    try {
        const response = await api.get<ApiResponse<number>>('/client/worker-amount');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const contractInfoAPI = async (contractId: number): Promise<ContractInfoResponseData> => { //클라이언트 계약 정보 조회 API 호출
    try {
        const response = await api.get<ApiResponse<ContractInfoResponseData>>('/contract/main/contract?contractId=' + contractId);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const currentPayAPI = async (): Promise<PaymentInfoResponseData> => { //클라이언트 최근 결제 단건 조회 API 호출
    try {
        const response = await api.get<ApiResponse<PaymentInfoResponseData>>('/payment/client/current');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const nextPayAPI = async (): Promise<PaymentInfoResponseData> => { //클라이언트 다음 결제 단건 조회 API 호출
    try {
        const response = await api.get<ApiResponse<PaymentInfoResponseData>>('/payment/client/next');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const chatAPI = async (chatRequest: ChatRequestFromFront) => { //챗봇 API 호출
    try {
        const response = await api.post<ApiResponse<string>>('/chat/once', chatRequest);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const paymentBarGraphAPI = async (year: number) => { //클라이언트 결제 통계 그래프 조회 API 호출
    try {
        const response = await api.get<ApiResponse<GetGraphResponseData[]>>('/payment/client/graph?year=' + year);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const paymentTypeAPI = async () => { //클라이언트 결제 방법 조회 API 호출
    try {
        const response = await api.get<ApiResponse<PaymentType>>('/client/payment');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const updatePaymentTypeAPI = async (paymentMethod: string): Promise<void> => { //클라이언트 결제 방법 수정 API 호출
    try {
        const response = await api.patch<ApiResponse<void>>('/client/payment?paymentMethod=' + paymentMethod);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다.');
        throw error;
    }
}

export const clientFirstPaymentAPI = async (): Promise<number> => { //특정 클라이언트 첫 결제 연도 조회 API 호출
    try {
        const response = await api.get<ApiResponse<number>>('/payment/client/first');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}

export const clientPaymentTableAPI = async (params: { page: number } //클라이언트 결제 테이블 조회 API 호출
): Promise<PageResponse<PaymentTableResponseData[]>> => {
    const searchParams = new URLSearchParams({page: params.page.toString()});
    try {
        const response = await api.get<ApiResponse<PageResponse<PaymentTableResponseData[]>>>(`/payment/client/table?${searchParams}`);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}

export const clientBillingCardAPI = async (authKey: string) => { //클라이언트 정기 결제 카드 등록 API 호출
    try {
        const response = await api.post<ApiResponse<void>>('/payment/client/card?authKey=' + authKey);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}

export const clientKeyAPI = async () => { //클라이언트 clientKey 조회 API 호출, 토스 페이먼츠 SDK 초기화에 필요
    try {
        const response = await api.get<ApiResponse<string>>('/payment/client/key');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}

export const payAPI = async (payRequest: PayRequest) => { //클라이언트 결제 API 호출
    try {
        const response = await api.post<ApiResponse<void>>('/payment/client', payRequest);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}

export const getInfoForPayAPI = async (): Promise<InfoForPayRequest> => { //결제에 사용할 클라이언트 정보 조회 API 호출
    try {
        const response = await api.get<ApiResponse<InfoForPayRequest>>('/client/pay');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}

export const deleteCardAPI = async () => { //클라이언트 정기 결제 카드 삭제 API 호출
    try {
        const response = await api.delete<ApiResponse<void>>('/payment/client/card');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}

export const getCardAPI = async (): Promise<CardInfoResponseData> => { //클라이언트 정기 결제 카드 조회 API 호출
    try {
        const response = await api.get<ApiResponse<CardInfoResponseData>>('/payment/client/card');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}

export const getReceiptAPI = async (paymentIds: number[]) => { //클라이언트 현금영수증 조회 API 호출
    try {
        const response = await api.get<ApiResponse<void>>('/payment/client/receipt?paymentIds=' + paymentIds);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}

export const getClientContractTableAPI = async (page: number): Promise<PageResponse<GetClientContractTableResponseData[]>> => { //클라이언트 계약 테이블 조회 API 호출
    try {
        const response = await api.get<ApiResponse<PageResponse<GetClientContractTableResponseData[]>>>('/contract/client/main?page=' + page);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}

export const createClientTaxInvoiceAPI = async (request: CreateClientTaxInvoiceRequest) => { //클라이언트 세금계산서 생성 API 호출
    try {
        const response = await api.post<ApiResponse<void>>('/tax/client', request);
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}

export const getClientPaidPayments = async (): Promise<GetClientPaidPaymentResponse[]> => { //클라이언트 결제된 결제 목록 조회 API 호출
    try {
        const response = await api.get<ApiResponse<GetClientPaidPaymentResponse[]>>('/payment/client/list');
        if (!response.data.success) {
            throw new Error(response.data.data.message);
        }
        return response.data.data;
    } catch (error) {
        handleApiError(error, '오류가 발생했습니다');
        throw error;
    }
}